import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@/contexts/UserContext";
import { teamsService } from "@/services/teamsService";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Users, Crown, UserCheck, Building } from "lucide-react";
import { UserPersona, TeamType } from "@/types/user";
import { toast } from "sonner";

export default function TeamTransition() {
  const navigate = useNavigate();
  const { user, updateUserProfile } = useUser();
  const [selectedRole, setSelectedRole] = useState<UserPersona | "">("");
  const [teamName, setTeamName] = useState("");
  const [teamType, setTeamType] = useState<TeamType>("general");
  const [organizationName, setOrganizationName] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleTransition = async () => {
    if (!selectedRole || !teamName.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!user?.id) {
      toast.error("User not found. Please refresh and try again.");
      return;
    }

    setIsLoading(true);
    
    try {
      // Step 1: Create team via API
      const teamResponse = await teamsService.createTeam({
        name: teamName.trim(),
        type: teamType,
        description: `${teamType} team created by ${user.name || user.email}`,
      });

      if (!teamResponse || !teamResponse.id) {
        throw new Error("Failed to create team");
      }

      const teamData = {
        id: teamResponse.id,
        name: teamResponse.name || teamName.trim(),
      };

      // Step 2: Add user as team member (backend handles this automatically when creating team as admin)
      // If user is admin, they're automatically added as admin member
      // Otherwise, explicitly add them
      if (selectedRole !== "manager" && selectedRole !== "organizationAdmin") {
        try {
          await teamsService.addMember(teamData.id, {
            userId: user.id,
            role: "member",
          });
        } catch (memberError) {
          console.warn("Could not add team member, proceeding:", memberError);
        }
      }

      // Step 3: Update user profile with new team info
      const updates: any = {
        persona: selectedRole,
        teamName: teamData.name,
        teamId: teamData.id,
        teamType,
        isNewUser: false,
        role: selectedRole === "manager" ? "manager" : "individual",
      };

      // Add organization details if organizationAdmin
      if (selectedRole === "organizationAdmin" && organizationName.trim()) {
        updates.organizationName = organizationName.trim();
        updates.organizationRole = "admin";
      }

      console.log("=== TEAM TRANSITION DEBUG ===");
      console.log("Updating user profile with:", updates);
      console.log("Selected role:", selectedRole);
      console.log("Team name:", teamName);
      console.log("Team ID:", teamData.id);
      
      await updateUserProfile(updates);
      
      // Wait a moment for the profile to update
      setTimeout(() => {
        toast.success("Successfully transitioned to team mode!");
        navigate("/");
      }, 1000);
    } catch (error) {
      console.error("Error transitioning to team mode:", error);
      toast.error("Failed to transition to team mode. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    {
      value: "newTeamMember" as UserPersona,
      label: "Team Member",
      description: "Collaborate as part of a team",
      icon: UserCheck,
    },
    {
      value: "manager" as UserPersona,
      label: "Team Manager",
      description: "Lead and manage a team",
      icon: Crown,
    },
    {
      value: "organizationAdmin" as UserPersona,
      label: "Organization Admin",
      description: "Manage teams across an organization",
      icon: Building,
    },
  ];

  return (
    <div className="max-w-2xl mx-auto py-8">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-full">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <h1 className="text-2xl font-bold">Transition to Team Mode</h1>
          </div>
          <p className="text-muted-foreground">
            Keep all your individual data and unlock team collaboration features
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Choose Your Role</CardTitle>
            <CardDescription>
              Select how you'll participate in team collaboration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-3">
              {roleOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <div
                    key={option.value}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedRole === option.value
                        ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                        : "border-muted hover:border-purple-300"
                    }`}
                    onClick={() => setSelectedRole(option.value)}
                  >
                    <div className="flex items-start gap-3">
                      <Icon className="h-5 w-5 text-purple-600 mt-0.5" />
                      <div>
                        <div className="font-medium">{option.label}</div>
                        <div className="text-sm text-muted-foreground">
                          {option.description}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <Separator />

            <div className="space-y-4">
              <div>
                <Label htmlFor="teamName">Team Name *</Label>
                <Input
                  id="teamName"
                  placeholder="e.g., Product Development Team"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="teamType">Functional Area</Label>
                <Select value={teamType} onValueChange={(value: TeamType) => setTeamType(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select functional area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="engineering">Engineering</SelectItem>
                    <SelectItem value="design">Design</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {selectedRole === "organizationAdmin" && (
                <div>
                  <Label htmlFor="organizationName">Organization Name *</Label>
                  <Input
                    id="organizationName"
                    placeholder="e.g., Acme Corp"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                  />
                </div>
              )}
            </div>

            <Separator />

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <h3 className="font-medium text-sm mb-2">What happens next?</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• All your individual progress and insights will be preserved</li>
                <li>• You'll gain access to team collaboration features</li>
                <li>• You can invite team members to join your workspace</li>
                <li>• Your dashboard will show team-focused insights</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => navigate("/")}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransition}
                disabled={!selectedRole || !teamName.trim() || isLoading}
                className="flex-1"
              >
                {isLoading ? "Transitioning..." : "Complete Transition"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
  );
}